import { createFileRoute, Link } from "@tanstack/react-router";
import { Waves, Bike, Fish, MapPin, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/things-to-do-near-crystal-beach-ontario")({
  head: () => ({
    meta: [
      { title: "Things to Do Near Crystal Beach - Malak BnB Resort" },
      {
        name: "description",
        content:
          "Bay Beach, cycling routes, the marina, and Niagara Falls day trips near Crystal Beach, Ontario. Stay at Malak BnB Resort just steps from it all.",
      },
      { property: "og:title", content: "Things to Do Near Crystal Beach - Malak BnB Resort" },
      {
        property: "og:description",
        content:
          "A local's guide to Bay Beach, cycling, fishing, and day trips from Crystal Beach, Ontario.",
      },
      {
        property: "og:url",
        content: "https://malakbnbresort.ca/blog/things-to-do-near-crystal-beach-ontario",
      },
      { property: "og:image", content: "/images/explore-crystal-beach.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          {
            name: "Things to Do Near Crystal Beach",
            path: "/blog/things-to-do-near-crystal-beach-ontario",
          },
        ]),
      },
    ],
    links: [canonical("/blog/things-to-do-near-crystal-beach-ontario")],
  }),
  component: ThingsToDoPage,
});

const FAQ = [
  {
    q: "What is there to do near Crystal Beach, Ontario?",
    a: "Crystal Beach offers Bay Beach for swimming and sunbathing, quiet cycling routes through town, Ridge Beach for a less crowded stretch of shoreline, a small marina for fishing, and easy access to Niagara Falls, Fort Erie, and Ridgeway. Malak BnB Resort sits five minutes from Bay Beach and makes a comfortable base for exploring all of it.",
  },
  {
    q: "How close is Malak BnB Resort to Bay Beach?",
    a: "Malak BnB Resort is a 5-minute walk from Bay Beach in Crystal Beach, Ontario. Guests can walk down for the morning and be back at the pool or on the patio by early afternoon.",
  },
  {
    q: "Is Malak BnB Resort close to Niagara Falls?",
    a: "Yes. Malak BnB Resort is about a 30-minute drive from Niagara Falls, which makes Crystal Beach a quieter, less expensive base for a Niagara day trip while still keeping the falls close.",
  },
  {
    q: "Is Malak BnB Resort cycling-friendly?",
    a: "Malak BnB Resort is in a cycling-friendly part of the Niagara Region, close to Cycle and Stay Niagara routes. The streets around Crystal Beach are flat and easy to ride, and the town itself is small enough to see almost entirely by bike.",
  },
];

function ThingsToDoPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/explore-crystal-beach.webp"
          alt="Crystal Beach town, Ontario on Lake Erie"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">
                Blog
              </Link>
              <span>/</span>
              <span>Things to Do Near Crystal Beach</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">
              A Local's Guide
            </p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Things to Do Near Crystal Beach, Ontario
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              Crystal Beach does not try very hard to impress you, and that is exactly why people
              keep coming back. It is a small lakeside town on the Canadian side of Lake Erie, in
              the Fort Erie area of the Niagara Region, and most of what makes it worth visiting is
              within walking or cycling distance of the water.
            </p>
            <p>
              You could spend a full weekend here without a car. Bay Beach for the mornings, the
              marina for the late afternoons, a bike ride through town whenever the mood strikes.
              And when you do want to go further, Niagara Falls is a short drive away, close enough
              for a day trip without needing to stay in the tourist core.
            </p>
            <p>Here is what to do, in roughly the order most people end up doing it.</p>
          </div>
        </div>
      </section>

      {/* Bay Beach */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl order-2 lg:order-1">
              <img
                src="/images/beach-bay.webp"
                alt="Bay Beach at Crystal Beach, Ontario on Lake Erie"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-3">
                <Waves className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">
                  Bay Beach
                </p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                A wide stretch of fine sand, right at the centre of town
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Bay Beach is the heart of Crystal Beach: soft sand, calm water in summer, and the
                smell of sunscreen and lake air drifting over from the boardwalk. It gets busy on
                hot July weekends and stays easy the rest of the time. Families set up early,
                swimmers wade out past the sandbar, and the whole scene has the unhurried feel of a
                small-town beach that has not been overdeveloped.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cycling */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Bike className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">
                  Cycling and the Town
                </p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                Flat streets, small shops, and no reason to rush
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Crystal Beach is flat, small, and genuinely enjoyable to cycle. A morning ride takes
                you past clapboard cottages, a handful of local shops and cafes, and quiet side
                streets that never see much traffic. The area connects to Cycle and Stay Niagara
                routes, so cyclists staying in Crystal Beach often use it as a base for longer rides
                into Fort Erie and Ridgeway.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/explore-biking.webp"
                alt="Cycling through the streets of Crystal Beach, Ontario"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ridge Beach */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl order-2 lg:order-1">
              <img
                src="/images/explore-ridge-beach.webp"
                alt="Ridge Beach shoreline near Crystal Beach on Lake Erie"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">
                  Ridge Beach
                </p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                For when Bay Beach fills up
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                A short walk or drive from the main strip, Ridge Beach is the quieter stretch of
                Lake Erie shoreline. Fewer people, more rocks and driftwood than fine sand, and the
                kind of view that makes you stop talking for a minute. Bring a chair and a book and
                stay past sunset if you can.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Marina and fishing */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Fish className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">
                  The Marina
                </p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                Cast a line or just watch the boats come in
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                The local marina is a good spot for an early morning or a slow evening. Fishing off
                the pier is popular with locals, and if you would rather watch than fish, there is
                something satisfying about seeing the boats head out at dawn and drift back in as
                the light changes over the lake.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/explore-fishing.webp"
                alt="Marina and fishing spot near Crystal Beach, Ontario"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Niagara Falls day trip */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
            Worth the Drive
          </p>
          <h2 className="text-3xl font-heading text-foreground mb-6">
            A Day Trip to Niagara Falls
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Niagara Falls is about a 30-minute drive from Crystal Beach. Plenty of visitors treat
            Crystal Beach as a quieter home base and drive in for the day, seeing the falls and the
            surrounding wineries without paying tourist-core hotel prices or dealing with the crowds
            at night. Fort Erie and Ridgeway are even closer, both worth a stop on the way back for
            dinner or a coffee.
          </p>
        </div>
      </section>

      {/* Pivot to Malak */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="/images/hero-exterior.webp"
                alt="Exterior of Malak BnB Resort in Crystal Beach, Ontario"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
                Where to Stay
              </p>
              <h2 className="text-3xl font-heading text-foreground leading-snug mb-6">
                A Base Close Enough to Walk to Bay Beach
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                After a day of beach, bikes, and the marina, Malak BnB Resort is a five-minute walk
                from Bay Beach and about 30 minutes from Niagara Falls. Four rooms, an outdoor
                heated pool, continental breakfast, and a family-owned team that reviews and
                confirms every reservation request personally.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                It is the kind of place that makes the rest of this list easy to reach without ever
                moving the car.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/book"
                  className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Request to Book
                </Link>
                <Link
                  to="/rooms"
                  className="inline-flex items-center justify-center rounded-full border border-border px-8 py-3 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                >
                  View Rooms and Rates
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">
              Common Questions
            </p>
            <h2 className="text-3xl font-heading text-foreground">Planning Your Visit</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="pb-4">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/explore" className="text-primary font-semibold text-sm hover:underline">
            See more of what's nearby on the Explore page →
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">
            Crystal Beach, Ontario
          </p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Plan your Crystal Beach trip around Malak BnB Resort
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send
            payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/book"
              className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg"
            >
              Request Your Stay
            </Link>
            <a
              href="tel:+12899298055"
              className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors"
            >
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
